/**
 * Chord Quality Drill — free-practice mode.
 *
 * Hear a chord (arpeggio → block), identify its quality.
 * No cadence — chord quality is context-free.
 * Pure — no I/O, no audio APIs, no DOM.
 */

import { AudioPlanBuilder, type AudioPlan } from "./audio-plan.js";
import { Rng } from "./exercise.js";
import { buildChord } from "./utils.js";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ChordQualitySymbol =
  | "maj" | "min" | "dim" | "aug"
  | "maj7" | "min7" | "dom7" | "min7b5" | "dim7"
  | "maj9" | "min9" | "dom9"
  | "maj11" | "min11" | "dom13"
  | "dom7b9" | "dom7s9" | "dom7s11" | "dom7b13";

export type ChordVoicingMode = "arpeggiated" | "blocked" | "mixed";
export type ChordInversionMode = "root-position" | "all-inversions";

export interface ChordQualityExerciseConfig {
  /** Chord qualities to draw from, ≥1. */
  qualities: ChordQualitySymbol[];
  voicing: ChordVoicingMode;
  inversion: ChordInversionMode;
  drillMode: "graded" | "listening";
  fsrsEnabled: boolean;
}

export interface ChordQualityExerciseRound {
  quality: ChordQualitySymbol;
  /** Root MIDI of the chord (before inversion). */
  rootMidi: number;
  /** Close-position chord tones in root order (no inversion applied). */
  closeMidis: number[];
  /** The MIDI notes as actually played (inversion applied). */
  playedMidis: number[];
  /** For extended (5+) chords, includes bass doubling for the block chord. */
  blockVoicingMidis: number[];
  /** Resolved voicing for this round (never "mixed"). */
  effectiveVoicing: "arpeggiated" | "blocked";
  /** 0 = root position, 1 = 1st inversion, etc. */
  inversionIndex: number;
  audioPlan: AudioPlan;
  teachAudio: AudioPlan;
}

// ─── Chord pool metadata ──────────────────────────────────────────────────────

export interface ChordQualityInfo {
  longName: string;
  /** Short symbol shown as sublabel on answer buttons. */
  shortSymbol: string;
  group: "triads" | "sevenths" | "extended" | "altered";
}

export const CHORD_QUALITY_POOL: Record<ChordQualitySymbol, ChordQualityInfo> = {
  maj:     { longName: "Major",             shortSymbol: "maj",    group: "triads" },
  min:     { longName: "Minor",             shortSymbol: "min",    group: "triads" },
  dim:     { longName: "Diminished",        shortSymbol: "dim",    group: "triads" },
  aug:     { longName: "Augmented",         shortSymbol: "aug",    group: "triads" },
  maj7:    { longName: "Major 7th",         shortSymbol: "maj7",   group: "sevenths" },
  min7:    { longName: "Minor 7th",         shortSymbol: "min7",   group: "sevenths" },
  dom7:    { longName: "Dominant 7th",      shortSymbol: "dom7",   group: "sevenths" },
  min7b5:  { longName: "Half-diminished",   shortSymbol: "m7♭5",   group: "sevenths" },
  dim7:    { longName: "Fully diminished",  shortSymbol: "°7",     group: "sevenths" },
  maj9:    { longName: "Major 9th",         shortSymbol: "maj9",   group: "extended" },
  min9:    { longName: "Minor 9th",         shortSymbol: "min9",   group: "extended" },
  dom9:    { longName: "Dominant 9th",      shortSymbol: "dom9",   group: "extended" },
  maj11:   { longName: "Major 11th",        shortSymbol: "maj11",  group: "extended" },
  min11:   { longName: "Minor 11th",        shortSymbol: "min11",  group: "extended" },
  dom13:   { longName: "Dom. 13th",         shortSymbol: "dom13",  group: "extended" },
  dom7b9:  { longName: "Dom. 7♭9",          shortSymbol: "7♭9",    group: "altered" },
  dom7s9:  { longName: "Dom. 7♯9",          shortSymbol: "7♯9",    group: "altered" },
  dom7s11: { longName: "Dom. 7♯11",         shortSymbol: "7♯11",   group: "altered" },
  dom7b13: { longName: "Dom. 7♭13",         shortSymbol: "7♭13",   group: "altered" },
};

/** Ordered list for display purposes (matches CHORD_QUALITY_POOL key order). */
export const CHORD_QUALITY_SYMBOLS_ORDERED: readonly ChordQualitySymbol[] = [
  "maj", "min", "dim", "aug",
  "maj7", "min7", "dom7", "min7b5", "dim7",
  "maj9", "min9", "dom9",
  "maj11", "min11", "dom13",
  "dom7b9", "dom7s9", "dom7s11", "dom7b13",
];

// ─── Groups (for the config checklist) ───────────────────────────────────────

export interface ChordQualityGroup {
  label: string;
  symbols: ChordQualitySymbol[];
}

export const CHORD_QUALITY_GROUPS: readonly ChordQualityGroup[] = [
  { label: "Triads",    symbols: ["maj", "min", "dim", "aug"] },
  { label: "7ths",      symbols: ["maj7", "min7", "dom7", "min7b5", "dim7"] },
  { label: "Extended",  symbols: ["maj9", "min9", "dom9", "maj11", "min11", "dom13"] },
  { label: "Altered",   symbols: ["dom7b9", "dom7s9", "dom7s11", "dom7b13"] },
];

// ─── Presets ──────────────────────────────────────────────────────────────────

export const CHORD_QUALITY_PRESETS: Record<"triads" | "triadsAndSevenths" | "all", ChordQualitySymbol[]> = {
  triads:           ["maj", "min", "dim", "aug"],
  triadsAndSevenths: ["maj", "min", "dim", "aug", "maj7", "min7", "dom7", "min7b5", "dim7"],
  all:              [...CHORD_QUALITY_SYMBOLS_ORDERED],
};

// ─── Default config ───────────────────────────────────────────────────────────

export const DEFAULT_CHORD_QUALITY_EXERCISE_CONFIG: ChordQualityExerciseConfig = {
  qualities: [...CHORD_QUALITY_PRESETS.triads],
  voicing: "arpeggiated",
  inversion: "root-position",
  drillMode: "graded",
  fsrsEnabled: false,
};

// ─── FSRS item ID ─────────────────────────────────────────────────────────────

/**
 * Map a chord quality to its curriculum item ID. Inversion mode is a difficulty
 * knob on the same skill (recognising the quality), not a separate skill — so
 * all-inversions drilling credits the same quality card rather than an orphaned
 * `exercise-chord::` card invisible to node/track mastery. (Inversion *identity*
 * is a different exercise — see `triad-inversions` / `chord-inversion-recognition`.)
 */
export function chordDrillItemId(
  quality: ChordQualitySymbol,
  _inversion: ChordInversionMode,
): string {
  if (["maj", "min", "dim", "aug"].includes(quality)) return `triad-quality::${quality}`;
  if (["maj7", "min7", "dom7", "min7b5", "dim7"].includes(quality)) return `seventh-chord-quality::${quality}`;
  if (["maj9", "min9", "dom9"].includes(quality)) return `extended-chords-9::${quality}`;
  if (["maj11", "min11", "dom13"].includes(quality)) return `extended-chords-11-13::${quality}`;
  return `altered-dominants::${quality}`;
}

// ─── Stats key ────────────────────────────────────────────────────────────────

/**
 * Stable key for DrillStatsRecord.
 * Format: `chord-drill|${quality}|${inversionMode}`
 */
export function chordDrillStatsKey(
  quality: ChordQualitySymbol,
  inversion: ChordInversionMode,
): string {
  return `chord-drill|${quality}|${inversion}`;
}

// ─── Audio helpers ────────────────────────────────────────────────────────────

function isExtendedQuality(quality: ChordQualitySymbol): boolean {
  return (
    ["maj9", "min9", "dom9", "maj11", "min11", "dom13",
     "dom7b9", "dom7s9", "dom7s11", "dom7b13"].includes(quality)
  );
}

/**
 * For 5+ note chords, double the root an octave below.
 * This anchors the root in the bass register so upper extensions sit in midrange.
 */
function voicedBlockChord(closeMidis: number[]): number[] {
  if (closeMidis.length <= 4) return closeMidis;
  return [closeMidis[0]! - 12, ...closeMidis];
}

/**
 * Apply inversion: move the first `inversionIndex` notes up an octave
 * so the chosen chord tone is the lowest note.
 */
function applyInversion(closeMidis: number[], inversionIndex: number): number[] {
  if (inversionIndex === 0) return closeMidis;
  const rotated = [...closeMidis];
  for (let i = 0; i < inversionIndex; i++) {
    rotated[i] = rotated[i]! + 12;
  }
  return rotated.sort((a, b) => a - b);
}

function buildChordArp(builder: AudioPlanBuilder, midis: number[]): void {
  for (const note of midis) {
    builder.addNote(note, 0.55, 0.20, "melody");
    builder.advance(0.30);
  }
  builder.advance(0.3);
}

function buildChordBlock(builder: AudioPlanBuilder, blockMidis: number[]): void {
  const gain = blockMidis.length >= 6 ? 0.10 : 0.13;
  builder.addChord(blockMidis, 1.5, gain, "harmony");
  builder.advance(1.6);
}

function buildTeachAudio(closeMidis: number[], blockMidis: number[]): AudioPlan {
  const builder = new AudioPlanBuilder();
  for (const note of closeMidis) {
    builder.addNote(note, 0.9, 0.22, "melody");
    builder.advance(1.0);
  }
  builder.advance(0.3);
  const gain = blockMidis.length >= 6 ? 0.10 : 0.13;
  builder.addChord(blockMidis, 1.8, gain, "harmony");
  builder.advance(1.9);
  return builder.build();
}

// ─── Main builder ─────────────────────────────────────────────────────────────

const CONCRETE_VOICINGS: ReadonlyArray<"arpeggiated" | "blocked"> = [
  "arpeggiated",
  "blocked",
];

export function buildChordQualityExerciseRound(
  config: ChordQualityExerciseConfig,
  seed: number,
): ChordQualityExerciseRound {
  const rng = new Rng(seed);

  const quality = rng.pick(config.qualities as ChordQualitySymbol[]);

  const effectiveVoicing: "arpeggiated" | "blocked" =
    config.voicing === "mixed"
      ? rng.pick(CONCRETE_VOICINGS as Array<"arpeggiated" | "blocked">)
      : config.voicing;

  // Extended chords sound better with a lower root so upper extensions stay in midrange.
  const rootMidi = isExtendedQuality(quality) ? 50 + rng.int(6) : 55 + rng.int(8);

  const closeMidis = buildChord(rootMidi, quality);

  // Inversion: pick a random chord tone as the bass note (not just triadic inversions).
  const maxInversionIndex = closeMidis.length - 1;
  const inversionIndex =
    config.inversion === "all-inversions" ? rng.int(maxInversionIndex + 1) : 0;

  const playedMidis = applyInversion(closeMidis, inversionIndex);
  const blockVoicingMidis = voicedBlockChord(playedMidis);

  const builder = new AudioPlanBuilder();

  if (effectiveVoicing === "arpeggiated") {
    buildChordArp(builder, playedMidis);
    buildChordBlock(builder, blockVoicingMidis);
  } else {
    // Blocked only: play block chord twice (once short, once sustained).
    const gain = blockVoicingMidis.length >= 6 ? 0.10 : 0.13;
    builder.addChord(blockVoicingMidis, 0.6, gain, "harmony");
    builder.advance(0.7);
    builder.addChord(blockVoicingMidis, 1.5, gain, "harmony");
    builder.advance(1.6);
  }

  return {
    quality,
    rootMidi,
    closeMidis,
    playedMidis,
    blockVoicingMidis,
    effectiveVoicing,
    inversionIndex,
    audioPlan: builder.build(),
    // Teach audio must reprise exactly what was presented — the inverted
    // voicing, not root position — or an all-inversions round replays
    // different pitches than the listener just heard.
    teachAudio: buildTeachAudio(playedMidis, blockVoicingMidis),
  };
}
